import { useCallback, useEffect, useMemo, useState } from "react";
import { useT } from "@/lib/i18n";
import { useBoard } from "./BoardContext";
import { zoomToBounds } from "./camera";
import { useMapKeys } from "./hooks";
import { useBoardUI } from "./store";

/**
 * Presentation mode: hides the chrome and walks through the board card by card (reading order:
 * top-to-bottom, left-to-right; a column counts as one step). Arrows/space/click advance, Esc exits.
 */
export function Presentation({ onExit }: { onExit: () => void }) {
  const { handle, index } = useBoard();
  const t = useT();
  const ids = useMapKeys(handle.types.cards);
  const steps = useMemo(() => {
    const cards = handle.types.cards;
    return ids
      .filter((id) => {
        const c = cards.get(id);
        return c && !c.get("parent") && !c.get("unsorted");
      })
      .map((id) => ({ id, x: cards.get(id)!.get("x") as number, y: cards.get(id)!.get("y") as number }))
      .sort((a, b) => Math.round(a.y / 80) - Math.round(b.y / 80) || a.x - b.x)
      .map((s) => s.id);
  }, [ids, handle]);
  const [i, setI] = useState(-1); // -1 = overview

  const show = useCallback(
    (n: number) => {
      const { w, h } = useBoardUI.getState().viewport;
      const ui = useBoardUI.getState();
      ui.clearSelection();
      if (n < 0) {
        const b = index.contentBounds();
        ui.setCamera(
          b
            ? zoomToBounds({ x: b.x - 40, y: b.y - 40, w: b.w + 80, h: b.h + 80 }, w, h, 40)
            : { x: 100, y: 100, z: 1 },
        );
        return;
      }
      const id = steps[n];
      const r = id ? index.rectOf(id) : null;
      if (!r) return;
      ui.setCamera(zoomToBounds({ x: r.x - 60, y: r.y - 60, w: r.w + 120, h: r.h + 120 }, w, h, 40, 1.6));
      ui.select([id!]);
    },
    [index, steps],
  );

  useEffect(() => {
    show(i);
  }, [i, show]);

  useEffect(() => {
    document.documentElement.requestFullscreen?.().catch(() => {});
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onExit();
      else if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown" || e.key === "Enter")
        e.preventDefault(), setI((v) => Math.min(steps.length - 1, v + 1));
      else if (e.key === "ArrowLeft" || e.key === "PageUp" || e.key === "Backspace")
        e.preventDefault(), setI((v) => Math.max(-1, v - 1));
      else if (e.key === "Home") setI(-1);
      else if (e.key === "End") setI(steps.length - 1);
    };
    const onFs = () => {
      if (!document.fullscreenElement) onExit();
    };
    window.addEventListener("keydown", onKey, { capture: true });
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      window.removeEventListener("keydown", onKey, { capture: true });
      document.removeEventListener("fullscreenchange", onFs);
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
      useBoardUI.getState().clearSelection();
    };
  }, [onExit, steps.length]);

  const btn = "rounded-sm px-3 py-1.5 text-sm hover:bg-panel-2 disabled:opacity-40";
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-4 z-40 flex justify-center">
      <div className="pointer-events-auto flex items-center gap-1 rounded-md border border-border bg-panel/95 p-1 shadow-panel backdrop-blur">
        <button type="button" className={btn} onClick={() => setI(-1)} title="Home">
          ⌂
        </button>
        <button
          type="button"
          className={btn}
          disabled={i <= -1}
          onClick={() => setI((v) => Math.max(-1, v - 1))}
        >
          ←
        </button>
        <span className="min-w-16 text-center text-xs text-muted">
          {i < 0 ? t.overview : `${i + 1} / ${steps.length}`}
        </span>
        <button
          type="button"
          className={btn}
          disabled={i >= steps.length - 1}
          onClick={() => setI((v) => Math.min(steps.length - 1, v + 1))}
        >
          →
        </button>
        <button type="button" className={`${btn} text-muted`} onClick={onExit} title="Esc">
          ✕ {t.exitPresentation}
        </button>
      </div>
    </div>
  );
}
